import { Component, ElementRef, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';

/**
 * DOVE-1928: reusable note body editor — a textarea plus a small toolbar with
 * Bold and Bullet buttons that insert/remove markdown syntax, so users can format
 * notes without typing markdown manually. Notes are still stored as markdown text
 * (the value is a plain string), keeping them compatible with the markdown renderer
 * used on the display surfaces.
 *
 * The toolbar buttons behave like Word/Outlook formatting buttons: they show an
 * active ("pressed") state when the cursor/selection is inside bold text or a
 * bullet line, and pressing them again removes that formatting.
 *
 * Implements ControlValueAccessor so it drops into existing forms exactly like the
 * previous textarea:
 *
 *   <app-note-markdown-editor formControlName="note" [rows]="6"
 *     placeholder="Enter your note"></app-note-markdown-editor>
 */
@Component({
	selector: 'app-note-markdown-editor',
	standalone: true,
	imports: [InputTextareaModule, TooltipModule],
	template: `
		<div class="note-markdown-editor">
			<div class="md-toolbar">
				<button
					type="button"
					class="md-toolbar-btn"
					[class.active]="boldActive"
					[disabled]="disabled"
					[attr.aria-pressed]="boldActive"
					(click)="toggleBold()"
					pTooltip="Bold"
					aria-label="Bold"
				>
					<strong>B</strong>
				</button>
				<button
					type="button"
					class="md-toolbar-btn"
					[class.active]="bulletActive"
					[disabled]="disabled"
					[attr.aria-pressed]="bulletActive"
					(click)="toggleBullet()"
					pTooltip="Bullet list"
					aria-label="Bullet list"
				>
					<i class="pi pi-list"></i>
				</button>
			</div>
			<textarea
				#textarea
				pInputTextarea
				class="w-full"
				[rows]="rows"
				[value]="value"
				[disabled]="disabled"
				[placeholder]="placeholder"
				(input)="onInput($any($event.target).value)"
				(keyup)="updateActiveState()"
				(click)="updateActiveState()"
				(select)="updateActiveState()"
				(blur)="onTouched()"
			></textarea>
		</div>
	`,
	styles: [
		`
			.md-toolbar {
				display: flex;
				gap: 0.25rem;
				margin-bottom: 0.5rem;
			}
			.md-toolbar-btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 2rem;
				height: 2rem;
				padding: 0;
				border: 1px solid transparent;
				border-radius: 4px;
				background: transparent;
				color: var(--text-color-secondary, #6c757d);
				cursor: pointer;
				font-size: 0.9rem;
				line-height: 1;
				transition:
					background-color 0.15s,
					border-color 0.15s,
					color 0.15s;
			}
			.md-toolbar-btn:hover:not(:disabled) {
				background: var(--surface-hover, rgba(0, 0, 0, 0.06));
			}
			.md-toolbar-btn.active {
				background: var(--highlight-bg, #eef2ff);
				border-color: var(--primary-color, #6366f1);
				color: var(--primary-color, #6366f1);
			}
			.md-toolbar-btn:disabled {
				opacity: 0.5;
				cursor: default;
			}
		`
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => NoteMarkdownEditorComponent),
			multi: true
		}
	]
})
export class NoteMarkdownEditorComponent implements ControlValueAccessor {
	@ViewChild('textarea') textareaRef!: ElementRef<HTMLTextAreaElement>;

	@Input() rows = 5;
	@Input() placeholder = '';

	value = '';
	disabled = false;
	boldActive = false;
	bulletActive = false;

	private onChange: (value: string) => void = () => {};
	onTouched: () => void = () => {};

	// ── ControlValueAccessor ──────────────────────────────────────────────────
	writeValue(value: string | null): void {
		this.value = value ?? '';
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	onInput(value: string): void {
		this.value = value;
		this.onChange(value);
		this.updateActiveState();
	}

	// ── Active ("pressed") state ──────────────────────────────────────────────
	/** Refresh whether the current selection/line is bold / a bullet, for the button state. */
	updateActiveState(): void {
		const el = this.textareaRef?.nativeElement;
		if (!el) return;
		this.boldActive = this.isSelectionBold(el.selectionStart, el.selectionEnd);
		this.bulletActive = this.isLineBulleted(el.selectionStart);
	}

	/**
	 * The **…** span (absolute marker positions) enclosing the cursor/selection on its
	 * line, or null. Scans the current line for ** pairs, so bold is detected from the
	 * cursor position alone, independent of any bullet prefix — which lets bold and
	 * bullet be active at the same time (e.g. "- **item**").
	 */
	private enclosingBoldSpan(start: number, end: number): { open: number; close: number } | null {
		const lineStart = this.value.lastIndexOf('\n', start - 1) + 1;
		const nl = this.value.indexOf('\n', end);
		const lineEnd = nl === -1 ? this.value.length : nl;
		const line = this.value.substring(lineStart, lineEnd);
		const relStart = start - lineStart;
		const relEnd = end - lineStart;

		const markers: number[] = [];
		for (let i = line.indexOf('**'); i !== -1; i = line.indexOf('**', i + 2)) {
			markers.push(i);
		}
		for (let p = 0; p + 1 < markers.length; p += 2) {
			const contentStart = markers[p] + 2;
			const contentEnd = markers[p + 1];
			const insideContent = relStart >= contentStart && relEnd <= contentEnd;
			const spansWhole = relStart <= markers[p] && relEnd >= markers[p + 1] + 2;
			if (insideContent || spansWhole) {
				return { open: lineStart + markers[p], close: lineStart + markers[p + 1] };
			}
		}
		return null;
	}

	private isSelectionBold(start: number, end: number): boolean {
		return this.enclosingBoldSpan(start, end) !== null;
	}

	private isLineBulleted(pos: number): boolean {
		const lineStart = this.value.lastIndexOf('\n', pos - 1) + 1;
		return this.value.startsWith('- ', lineStart);
	}

	// ── Toolbar actions (toggle on/off, Word-style) ───────────────────────────
	toggleBold(): void {
		const el = this.textareaRef.nativeElement;
		const start = el.selectionStart;
		const end = el.selectionEnd;
		const span = this.enclosingBoldSpan(start, end);

		if (span) {
			// Already bold → remove the enclosing ** markers.
			const inner = this.value.slice(span.open + 2, span.close);
			const next = this.value.slice(0, span.open) + inner + this.value.slice(span.close + 2);
			if (start <= span.open) {
				// Selection wrapped the whole span → keep the unwrapped content selected.
				this.commit(next, span.open, span.open + inner.length);
			} else {
				// Cursor/selection was inside the content → shift left past the removed opener.
				this.commit(next, start - 2, end - 2);
			}
		} else {
			// Not bold → wrap the selection (or insert a placeholder).
			const inner = this.value.substring(start, end) || 'bold';
			this.commit(
				this.value.slice(0, start) + '**' + inner + '**' + this.value.slice(end),
				start + 2,
				start + 2 + inner.length
			);
		}
	}

	toggleBullet(): void {
		const el = this.textareaRef.nativeElement;
		const start = el.selectionStart;
		const end = el.selectionEnd;

		const lineStart = this.value.lastIndexOf('\n', start - 1) + 1;
		const afterEnd = this.value.indexOf('\n', end);
		const lineEnd = afterEnd === -1 ? this.value.length : afterEnd;

		const lines = this.value.substring(lineStart, lineEnd).split('\n');
		const allBulleted = lines.every((line) => line.startsWith('- '));
		const result = (
			allBulleted
				? lines.map((line) => line.replace(/^- /, ''))
				: lines.map((line) => (line.startsWith('- ') ? line : '- ' + line))
		).join('\n');

		this.commit(
			this.value.slice(0, lineStart) + result + this.value.slice(lineEnd),
			lineStart,
			lineStart + result.length
		);
	}

	private commit(next: string, selStart: number, selEnd: number): void {
		this.value = next;
		this.onChange(next);
		// Restore focus + selection after the value binding re-renders, then refresh state.
		setTimeout(() => {
			const el = this.textareaRef.nativeElement;
			el.focus();
			el.setSelectionRange(selStart, selEnd);
			this.updateActiveState();
		});
	}
}
